import React, { useEffect, useRef } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_URL, AUTH_ERR_MSG, CFCProps, USERSROLE, AUTH_TYPE } from 'types';
import { UnAuthenticatedPageLayout } from 'layouts/unAuthenticatedPage.layout';
import Utils from 'utilities/helper.util';
import { RootState } from 'redux/root.reducer';
import { fetchUserStart } from 'redux/user/actions';
import { fetchEnquiryHistory } from 'redux/enquiries';
import { setAppLoginType } from 'redux/app/actions';
import { resetLoginError } from 'redux/auth/action';
import { ENQUIRY_ERR_MSG } from 'types/enquiry.type';
import {
  setUserSession,
  setAuthenticated,
  setRedirectPath
} from 'redux/session/actions';
import {
  isSafeAppPath,
  redirectResidentialEnquirerIfNeeded,
  toLocationShape,
  toPendingPath
} from 'utilities/residential-redirect.util';

export const Auth: React.FC<Props> = (props: Props) => {
  const dispatch: any = useDispatch();
  const { app, session, login, enquiresCache } = props;
  const { error }: any = login;
  const { loginType, userRole } = app;
  const history = useHistory();
  const location = useLocation<{ from?: Location }>();
  const { isAuthenticated, currentUser, redirectPath } = session;
  const initialize = useRef(false);

  const localStorageObjs = window?.opener?.localStorage;

  const customLoginType = window?.sessionStorage?.getItem('custom_login_type');

  /**
   * Capture ?redirectTo= from residential (and other) deep links before login.
   * Clear any stale path when landing on bare /auth so a previous deep link
   * cannot leak into a later login with no return URL.
   *
   * WHY effect: Cognito round-trip drops the query string, so we must store the
   * path in redux (and pass it as location.state.from) before navigating to Login.
   * Skip clearing when `code` is present so an OAuth callback is not raced
   * against customOAuthState restoring the return path.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirectTo');
    if (redirectTo && isSafeAppPath(redirectTo)) {
      dispatch(setRedirectPath(redirectTo));
    } else if (!params.has('code')) {
      dispatch(setRedirectPath(undefined));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    if (customLoginType) {
      dispatch(setAppLoginType(customLoginType));
      window.sessionStorage.removeItem('custom_login_type');
    }
  }, [customLoginType, dispatch]);

  useEffect(() => {
    if (localStorageObjs) {
      let currentUser = '';
      let appObj: any = '';
      Object.keys(localStorageObjs).forEach((key) => {
        const value = localStorageObjs.getItem(key);

        if (key.includes('persist:root')) {
          var valObj = JSON.parse(value);
          const userSession = valObj?.session;
          const userSessionObj = JSON.parse(userSession);
          currentUser = userSessionObj.currentUser;
          const app = valObj?.app;
          appObj = JSON.parse(app);
        }

        // set the local storage in the new
        localStorage.setItem(key, value);
      });
      // set the user session
      if (currentUser) {
        dispatch(setAppLoginType(appObj?.loginType || null));
        dispatch(setUserSession(currentUser));
        dispatch(setAuthenticated(true));
        if (location?.state) {
          const redirectPath = location?.state?.from?.pathname;
          history.push({
            pathname: redirectPath
          });
        }
      }
    }
    // eslint-disable-next-line
  }, [localStorageObjs, dispatch]);

  useEffect(() => {
    dispatch(fetchUserStart());
  }, [dispatch]);

  useEffect(() => {
    const queryParams = {
      fields: 'id%2CcreatedAt%2CexternalId%2CuserReference',
      include: 'Address',
      orderBy: '-createdAt',
      limit: 1,
      offset: 0,
      filter: `userId:${currentUser.id}`
    };
    currentUser.id && dispatch(fetchEnquiryHistory(queryParams));
  }, [dispatch, currentUser.id]);

  // Check authentication
  useEffect(() => {
    if (!initialize.current) {
      initialize.current = true;
      return;
    }

    // external app page redirect to logout user
    if (
      isAuthenticated &&
      loginType === AUTH_TYPE.EXTERNAL_APP_LOGIN &&
      window.self === window.top
    ) {
      history.push(APP_URL.EXTERNAL_APP);
      return;
    }

    // Terms/multi-org page redirect
    if (error) {
      const errorMessage = error.message;
      if (errorMessage === AUTH_ERR_MSG.USER_ACCEPT_TERMS)
        history.push(APP_URL.AUTH_USER_TERMS);
      if (errorMessage === AUTH_ERR_MSG.USER_SESSION_ORGANISATION)
        history.push(APP_URL.AUTH_MULTI_ORGANISATION);

      if (error === ENQUIRY_ERR_MSG.NOT_FOUND) {
        dispatch(resetLoginError());
        history.push(APP_URL.DASHBOARD);
      }
    }

    // Dashboard/login page redirect
    if (
      isAuthenticated &&
      userRole !== USERSROLE.OTHER &&
      !Utils.isObjectEmpty(currentUser) &&
      !Utils.isObjectEmpty(enquiresCache) &&
      enquiresCache.data.enquiries &&
      !error
    ) {
      if (
        redirectResidentialEnquirerIfNeeded(
          userRole,
          currentUser,
          redirectPath || toPendingPath(location.state?.from)
        )
      ) {
        return;
      }
      if (redirectPath) {
        history.push(redirectPath);
      } else if (
        enquiresCache.data.enquiries?.items.length === 0 &&
        userRole === USERSROLE.ENQUIRER
      ) {
        history.push(APP_URL.LODGE_NEW_ENQUIRY);
      } else {
        history.push(APP_URL.DASHBOARD);
      }
    }

    if (
      loginType === null &&
      !isAuthenticated &&
      Utils.isObjectEmpty(currentUser)
    ) {
      const pendingFrom = location.state?.from || toLocationShape(redirectPath);
      if (window.self !== window.top) {
        let authMethod = 'normal';
        if (pendingFrom?.search) {
          const params = new URLSearchParams(pendingFrom.search);
          authMethod = params.get('authMethod') || 'normal';
        }
        if (authMethod.trim().toUpperCase() === 'OTP') {
          dispatch(setAppLoginType(AUTH_TYPE.OPT_LOGIN));
          history.push({
            pathname: APP_URL.ONE_TIME_PASSCODE,
            state: {
              from: pendingFrom
            }
          });
        } else {
          history.push({
            pathname: APP_URL.LOGIN,
            state: {
              from: pendingFrom
            }
          });
        }
      } else {
        history.push({
          pathname: APP_URL.LOGIN,
          state: {
            from: pendingFrom
          }
        });
      }
    }
  }, [
    error,
    isAuthenticated,
    userRole,
    loginType,
    currentUser,
    history,
    location,
    redirectPath,
    enquiresCache,
    dispatch
  ]);

  return (
    <div id="auth" data-testid="auth">
      <UnAuthenticatedPageLayout>
        {/* Empty page place holder for page redirect  */}
      </UnAuthenticatedPageLayout>
    </div>
  );
};

Auth.displayName = 'Auth';

const mapStateToProps = ({
  app,
  session,
  login,
  enquiresCache
}: RootState) => ({
  app,
  session,
  login,
  enquiresCache
});

const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector> & { redirectPath: string };
export default React.memo(connector(Auth));
